import { showSuccess, showError } from '@calendly/alert'
import { I18n } from '@calendly/i18n'

import { rootApi } from 'root-api'

import { INITIAL_PAGE_SIZE, PAGE_SIZE } from './constants'

const handleDeleteDataStatus = async (_data, { dispatch, queryFulfilled }) => {
  try {
    await queryFulfilled
    dispatch(showSuccess(I18n.t('publisher.account_settings.meeting_intelligence.modal.delete.success')))
  } catch (e) {
    dispatch(showError(I18n.t('publisher.flash.error')))
  }
}

const api = rootApi.enhanceEndpoints({
  addTagTypes: [
    'MeetingIntelligenceUserSettings',
    'TrackedMeetings',
    'MeetingIntelligenceScheduledTrackedEventExistsWithProvider',
    'HubspotAccountForMi',
    'NotetakerFreeMeetingsQuota',
    'SalesforceAccountForMi'
  ]
}).injectEndpoints({
  endpoints: (build) => ({
    getMeetingIntelligenceDefaultUserSettings: build.query({
      query: () => ({ url: '/api/meeting_intelligence_settings/show_default_user_settings' }),
      extraOptions: {
        handleErrors: false
      },
    }),
    getMeetingIntelligenceUserSettings: build.query({
      query: () => ({ url: '/api/meeting_intelligence_settings/show_user_settings' }),
      extraOptions: {
        handleErrors: false
      },
      providesTags: ['MeetingIntelligenceUserSettings']
    }),
    updateUserMeetingIntelligenceData: build.mutation({
      query: (data) => ({
        url: '/api/meeting_intelligence_settings/update_user_settings',
        method: 'POST',
        data
      }),
      invalidatesTags: ['MeetingIntelligenceUserSettings', 'TrackedMeetings'],
    }),
    setMeetingIntelligenceFeatureFlagOverride: build.mutation({
      query: () => ({
        url: '/api/meeting_intelligence_settings/set_meeting_intelligence_feature_flag_override',
        method: 'POST',
      }),
    }),
    updateOrganizationMeetingIntelligenceData: build.mutation({
      query: (data) => ({
        url: '/api/meeting_intelligence_settings/update_organization_settings',
        method: 'POST',
        data
      }),
      extraOptions: {
        handleErrors: false
      },
      invalidatesTags: ['MeetingIntelligenceUserSettings', 'TrackedMeetings'],
    }),
    deleteMeetingIntelligenceUserMeetingData: build.mutation({
      query: () => ({
        url: '/api/meeting_intelligence_settings/destroy_artifacts',
        method: 'DELETE',
      }),
      onQueryStarted: handleDeleteDataStatus,
      invalidatesTags: ['TrackedMeetings'],
    }),
    getUserCalendars: build.query({
      query: () => ({
        url: '/api/calendar_accounts'
      })
    }),
    getEvents: build.query({
      query: () => ({
        url: '/api/users/me/filters/event_type/bootstrap',
        method: 'POST',
        data: {
          term: null,
          withDeleted: 1,
          initialPageSize: INITIAL_PAGE_SIZE,
          pageSize: PAGE_SIZE,
          includeTotalCount: true,
        }
      })
    }),
    getMoreEvents: build.query({
      query: (params) => ({
        url: '/api/users/me/filters/event_type/items',
        params: {
          ...params,
          withDeleted: 1,
          initialPageSize: INITIAL_PAGE_SIZE,
          pageSize: PAGE_SIZE,
        }
      })
    }),
    getScheduledTrackedEventExistsWithProvider: build.query({
      query: ({ provider }) => ({
        url: `/api/meeting_intelligence_settings/show_scheduled_tracked_event_exists_with_conference_provider/${provider}`
      }),
      extraOptions: {
        handleErrors: false
      },
      providesTags: ['MeetingIntelligenceScheduledTrackedEventExistsWithProvider'],
    }),
    getNotetakerFreeMeetingsQuota: build.query({
      query: ({ userUuid }) => ({
        url: '/api/organization/usage_metering/feature_availability',
        params: {
          feature_key: 'notetaker_free_meetings_1',
          subject_uuid: userUuid,
          subject_type: 'User',
        }
      }),
      providesTags: ['NotetakerFreeMeetingsQuota']
    }),
    fetchHubspotAccountForMi: build.query({
      query: () => ({
        url: '/api/integrations/hubspot/account'
      }),
      providesTags: ['HubspotAccountForMi']
    }),
    fetchSalesforceAccountForMi: build.query({
      query: () => ({
        url: '/api/integrations/salesforce/account'
      }),
      providesTags: ['SalesforceAccountForMi']
    }),
    updateHubspotAccountForMi: build.mutation({
      query: ({ syncMiNotes, syncEvents, syncStatus }) => ({
        url: '/api/integrations/hubspot/account',
        method: 'PATCH',
        data: { account: { syncMiNotes, syncEvents, syncStatus } },
      }),
      invalidatesTags: ['HubspotAccountForMi'],
    }),
    updateSalesforceAccountForMi: build.mutation({
      query: ({ meetingNotesEnabled }) => ({
        url: '/api/integrations/salesforce/account/meeting_notes',
        method: 'PATCH',
        data: { meetingNotesEnabled },
      }),
      invalidatesTags: ['SalesforceAccountForMi'],
    }),
  }),
  overrideExisting: true,
})

export const {
  useGetMeetingIntelligenceUserSettingsQuery,
  useDeleteMeetingIntelligenceUserMeetingDataMutation,
  useSetMeetingIntelligenceFeatureFlagOverrideMutation,
  useUpdateUserMeetingIntelligenceDataMutation,
  useUpdateOrganizationMeetingIntelligenceDataMutation,
  useGetUserCalendarsQuery,
  useGetEventsQuery,
  useLazyGetMoreEventsQuery,
  useGetMeetingIntelligenceDefaultUserSettingsQuery,
  useGetNotetakerFreeMeetingsQuotaQuery,
  useFetchHubspotAccountForMiQuery,
  useFetchSalesforceAccountForMiQuery,
  useUpdateHubspotAccountForMiMutation,
  useUpdateSalesforceAccountForMiMutation,
} = api
